import React from 'react';
import {ScrollView} from '@genestack/ui';
import {RightPanelListItem} from '../right-panel-list-item';
import {useFoldingItemsState} from '../../../../../../hooks/use-folding-items-state';
import {EmptyList} from '../../../../../../components/empty-list/empty-list';
import {NothingFoundView} from './nothing-found-view';
import {matchString, SearchResultChildrenMaps} from './use-filter-stats';
import {MatchedSynonym} from './matched-synonym';
import {ShowMoreButton} from './show-more-button';
import {ContinuantTypeStatRecord} from '../interface';
import {FoldingItem} from '../folding-item';

interface Props {
    selectedGraphNodes: number[];
    setHoveredNodes: (nodeIds: number[]) => void;
    selectGraphNodes: (nodes: number[], isSelectMultiple: boolean) => void;
    continuantTypes: ContinuantTypeStatRecord[];
    searchString: string;
    searchResultChildrenMaps?: SearchResultChildrenMaps;
    foldingStateProps: ReturnType<typeof useFoldingItemsState>;
    irrelevantShownProps: ReturnType<typeof useFoldingItemsState>;
}

export function ContinuantsStatistics(props: Props) {
    function getFoldedGroupSettings(continuantType: ContinuantTypeStatRecord) {
        if (props.searchString && props.searchResultChildrenMaps) {
            const [matchedChildren, irrelevantChildren] = [
                props.searchResultChildrenMaps.continuantTypesMatchedChildren[
                    continuantType.label
                ] || [],
                props.searchResultChildrenMaps.continuantTypesIrrelevantChildren[
                    continuantType.label
                ] || []
            ];
            const groupNameMatches = matchString(continuantType.label, props.searchString);
            const hasShowMoreButton =
                groupNameMatches && !!matchedChildren.length && !!irrelevantChildren.length;

            const isIrrelevantChildrenShown = hasShowMoreButton
                ? props.irrelevantShownProps.foldingState[continuantType.label]
                : groupNameMatches;

            return {
                renderedChildren: [
                    ...matchedChildren,
                    ...(isIrrelevantChildrenShown ? irrelevantChildren : [])
                ],
                hasShowMoreButton,
                isUnfoldedByDefault: !!matchedChildren.length
            };
        }

        return {
            renderedChildren: continuantType.continuants,
            hasShowMoreButton: false,
            isUnfoldedByDefault: false
        };
    }

    if (!Object.keys(props.continuantTypes).length) {
        if (props.searchString) {
            return <NothingFoundView searchString={props.searchString} />;
        }

        return <EmptyList message="Nothing found" />;
    }

    return (
        <ScrollView>
            {props.continuantTypes.map((continuantType) => {
                const {renderedChildren, isUnfoldedByDefault, hasShowMoreButton} =
                    getFoldedGroupSettings(continuantType);

                const isIrrelevantChildrenShown =
                    props.irrelevantShownProps.foldingState[continuantType.label];

                return (
                    <FoldingItem
                        key={continuantType.label}
                        label={continuantType.label}
                        count={continuantType.occurrencesCount}
                        foldingStateProps={props.foldingStateProps}
                        foldingStateKey={continuantType.label}
                        padding={1}
                        searchString={props.searchString}
                        isUnfoldedByDefault={isUnfoldedByDefault}
                        declarationUrl={
                            continuantType.documentAccession &&
                            continuantType.elementNodeId &&
                            `/documents/edit/${continuantType.documentAccession}/@${continuantType.elementNodeId}`
                        }
                    >
                        {renderedChildren.map((continuantStatRecord) => (
                            <RightPanelListItem
                                label={continuantStatRecord.label}
                                count={continuantStatRecord.occurrencesCount}
                                key={`${continuantStatRecord.documentAccession}${continuantStatRecord.elementNodeId}`}
                                active={
                                    // if graph is empty graphViewNodeIds length is 0
                                    !!props.selectedGraphNodes.length &&
                                    continuantStatRecord.graphViewNodeIds.every((id) =>
                                        props.selectedGraphNodes.includes(id)
                                    )
                                }
                                setHoveredNodes={props.setHoveredNodes}
                                selectGraphNodes={props.selectGraphNodes}
                                graphViewNodeIds={continuantStatRecord.graphViewNodeIds}
                                padding={2}
                                searchString={props.searchString}
                                append={
                                    <MatchedSynonym
                                        continuant={continuantStatRecord}
                                        searchValue={props.searchString}
                                    />
                                }
                                declarationUrl={`/documents/edit/${continuantStatRecord.documentAccession}/@${continuantStatRecord.elementNodeId}`}
                            />
                        ))}

                        {hasShowMoreButton && (
                            <ShowMoreButton
                                isShowingMore={isIrrelevantChildrenShown}
                                onClick={() => {
                                    props.irrelevantShownProps.toggleFoldingState(
                                        continuantType.label
                                    );
                                }}
                                level={2}
                            />
                        )}
                    </FoldingItem>
                );
            })}
        </ScrollView>
    );
}
